import { clsx } from 'clsx';
import { Tabs, TabsProps } from 'antd';
import React, { useEffect, useState } from 'react';
import { Edit, EditProps, useResourceContext } from 'react-admin';

import { Dealer, DealerType } from '@types';
import { Forbidden, PageHeader } from '@components';
import { Marketplace } from '@pages/dealer/Marketplace';
import { DealerAccount } from '@pages/dealer/DealerAccount';
import { SharedFarmers } from '@pages/dealer/SharedFarmers';
import { DealerDocuments } from '@pages/dealer/DealerDocuments';
import { DealerForm } from '@pages/dealer/DealerForm/DealerForm';
import { useEditProps, useQuery, useRbacEdit, useRbacView, useRecord } from '@hooks';
import cls from './DealerEdit.module.css';


export const DealerEdit = (props: EditProps) => {
  const dealer = useRecord<Dealer>(props);
  const resource = useResourceContext();
  const rbacView = useRbacView();
  const rbacEdit = useRbacEdit();
  const editProps = useEditProps();
  const [activeTab, setActiveTab] = useState<string>('1');
  const query = useQuery();
  const tabFromQuery = query.get('tab');
  const isAssociate = dealer?.type === DealerType.ASSOCIATE;

  useEffect(() => {
    if (tabFromQuery) {
      setActiveTab(tabFromQuery);
    }
  }, [tabFromQuery]);

  if (!dealer) {
    return null;
  }
  if (!rbacView) {
    return <Forbidden />;
  }

  const hasManager = !!dealer?.manager?.id;
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Company Information',
      children: <DealerForm editPage canEdit={rbacEdit} />,
    },
    {
      key: '2',
      label: 'Company Documents',
      children: <DealerDocuments />,
    },
    {
      key: '3',
      label: !hasManager ? 'Create Account'
        : dealer.type === DealerType.BUYER ? 'Empresas Account'
          : dealer.type === DealerType.RETAILER ? 'Negocios Account' : 'Socios Account',
      children: <DealerAccount props={props} />,
    },
  ];

  hasManager && items.push({
    key: '4',
    label: 'Shared Farmers',
    children: <SharedFarmers dealer={dealer} />,
  });

  dealer.manager?.membership === 'Plus' && items.push({
    key: '5',
    label: 'Marketplace',
    children: <Marketplace dealer={dealer} />,
  });

  const getBackLinkText = () => {
    if (resource === 'dealer-retailer') {
      return 'Back to all Retailers';
    }
    if (resource === 'dealer-associate') {
      return 'Back to all Associates';
    }
    return 'Back to all Off Takers';
  };

  const getBackLinkUrl = () => {
    if (resource === 'dealer-retailer') {
      return '/dealer-retailer';
    }
    if (resource === 'dealer-associate') {
      return '/dealer-associate';
    }
    return '/dealer';
  };

  const getTitle = () => {
    if (resource === 'dealer-retailer') {
      return 'Retailer';
    }
    if (resource === 'dealer-associate') {
      return 'Associate';
    }
    return 'Off Taker';
  };

  return (
    <Edit
      {...props}
      title={getTitle()}
      {...editProps}
      className={clsx(cls.page, 'details-page-wrapper')}
    >
      <PageHeader
        title={getTitle()}
        linkText={getBackLinkText()}
        linkUrl={getBackLinkUrl()}
      />
      <Tabs
        className={cls.tabs}
        type="card"
        items={items}
        activeKey={activeTab}
        onChange={activeTab => setActiveTab(activeTab)}
      />
    </Edit>
  );
};
